import { gql, useMutation } from "@apollo/client";
import { Header, useLanguageTranslator } from "../../../../components";
import { AdminView } from "../features/adminView";
import { useApolloQuery } from "../../../../hooks";
import { useEffect, useMemo, useState } from "react";
import { statusCode, toastTypesKeys } from "../../../../utils/constants";
import { toastHandler } from "../../../../utils/helpers";
import { ToastContainer } from "react-toastify";
import { useAuthSession } from "../../../../components/AuthSessionProvider";
import { ConfirmationDialogs } from "../features";

const pendingPeopleDocument = gql`
  query ($limit: Int, $page: Int) {
    pendingPeople(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        firstname
        id
        lastname
        picture {
          source
          originalFilename
        }
        status
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const noPendingDocument = gql`
  query ($limit: Int, $page: Int) {
    noPendingPeople(limit: $limit, page: $page) {
      collection {
        country {
          name
        }
        createdAt
        firstname
        id
        lastname
        picture {
          source
          originalFilename
        }
        status
        title
        updatedAt
      }
      metadata {
        currentPage
        limitValue
        totalCount
        totalPages
      }
    }
  }
`;

const peopleStatusUpdate = gql`
  mutation UpdatePersonStatus($personInput: PersonStatusUpdateInput!) {
    personStatusUpdate(input: $personInput) {
      clientMutationId
      person {
        country {
          name
        }
        createdAt
        firstname
        id
        lastname
        picture {
          source
          originalFilename
        }
        status
        title
        updatedAt
      }
    }
  }
`;

function AdminPeople() {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState<any>({
    status: false,
    currId: null,
    moduleCode: 2,
  });
  const [rejectReason, setRejectReason] = useState("");
  const [isError, setIsError] = useState(false);

  const { user } = useAuthSession() as any;
  const { translator } = useLanguageTranslator();

  const { data: pending } = useApolloQuery({
    queryDocument: pendingPeopleDocument,
    isPrivateCall: true,
    variables: {
      limit: 8,
    },
  });

  const {
    data: noPending,
    loading,
    refetch,
  } = useApolloQuery({
    queryDocument: noPendingDocument,
    isPrivateCall: true,
    variables: {
      limit: 8,
    },
  });

  const [peopleStatus] = useMutation(peopleStatusUpdate, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  async function onClickHandler(peopleID: number) {
    if (isOpen?.moduleCode === statusCode.approved) {
      try {
        const response = await peopleStatus({
          variables: {
            personInput: { id: Number(peopleID), status: "Approved" },
          },
        });
        if (response?.data) {
          setIsOpen({ status: false, currId: null, module: "" });
          toastHandler("Status is Updated", toastTypesKeys.success);
          setTimeout(() => {
            refetch();
          }, 2300);
        }
      } catch (error) {
        console.error("Error:", error);
        setIsOpen({ status: false, currId: null, module: "" });
        setIsError(true);
      }
      return;
    }

    if (!rejectReason)
      return toastHandler("Please enter the Reason", toastTypesKeys.error);

    try {
      const response = await peopleStatus({
        variables: {
          personInput: {
            id: Number(peopleID),
            status: "Rejected",
            reason: rejectReason,
          },
        },
      });
      if (response?.data) {
        setIsOpen({ status: false, currId: null, module: "" });
        toastHandler("Status is Updated", toastTypesKeys.success);
        setTimeout(() => {
          refetch();
        }, 2300);
        setRejectReason("");
      }
    } catch (error) {
      console.error("Error:", error);
      setIsOpen({ status: false, currId: null, module: "" });
      setRejectReason("");
      setIsError(true);
    }
  }

  const filteredPendingPeopleList = useMemo(() => {
    if (!inputValue) return pending?.pendingPeople?.collection || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return pending?.pendingPeople?.collection?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, pending?.pendingPeople?.collection]);

  const filteredNoPendingPeopleList = useMemo(() => {
    if (!inputValue) return noPending?.noPendingPeople?.collection || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return noPending?.noPendingPeople?.collection?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, noPending?.noPendingPeople?.collection]);

  function onAdminActions(id: any, type: any) {
    setIsOpen({ status: true, currId: id, moduleCode: type });
  }

  return (
    <>
      <ToastContainer />
      <Header
        title={translator("Admin Area")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />
      <ConfirmationDialogs
        text="people"
        viewPath={`/admin/person_detail?id=${isOpen?.currId}`}
        isOpen={isOpen}
        onClickHandler={onClickHandler}
        setIsOpen={setIsOpen}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        isError={isError}
        setIsError={setIsError}
      />
      <AdminView
        title={translator("People")}
        viewPath={`/admin/person_detail?id=`}
        viewAllSubmissionsPath="/admin/people/view_all"
        isAdminPeople={true}
        pendingData={filteredPendingPeopleList || []}
        noPendingData={filteredNoPendingPeopleList || []}
        onAdminActions={onAdminActions}
      />
    </>
  );
}

export { AdminPeople };
